import { Controller } from 'react-hook-form';
import { TextField, FormControlLabel, Switch, Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Select, MultiSelect, JsonEditor, FormField } from '@shared/components';
import type { DynamicFieldConfig } from '../types';

export interface DynamicFieldRendererProps {
  control: any;
  fields: DynamicFieldConfig[];
  errors?: any;
  setError?: any;
  clearErrors?: any;
}

export function DynamicFieldRenderer({ control, fields, errors, setError, clearErrors }: DynamicFieldRendererProps) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      {fields.map((f, index) => (
        <Controller
          key={`${f.key}_${index}`}
          name={`custom_fields.${f.key}`}
          rules={{ required: !!f.required }}
          control={control}
          render={({ field }) => {
            const common = {
              fullWidth: true,
              required: !!f.required,
              error: !!errors?.custom_fields?.[f.key],
              helperText: errors?.custom_fields?.[f.key]?.message,
            };

            switch (f.type) {
              case 'text':
              case 'number':
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <TextField
                      {...field}
                      type={f.type}
                      // placeholder={f.label}
                      {...common}
                      InputLabelProps={{ shrink: false }}
                    />
                  </Box>
                );
              case 'boolean':
                // Safely parse boolean value, defaulting to false
                let boolValue = false;
                try {
                  boolValue = typeof field.value === 'boolean' ? field.value : JSON.parse(field?.value || 'false');
                } catch (e) {
                  boolValue = false;
                }
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <FormControlLabel
                      control={<Switch checked={boolValue} onChange={(e) => field.onChange(e.target.checked)} />}
                      label=""
                    />
                  </Box>
                );
              case 'date':
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <DatePicker
                      value={field.value || null}
                      onChange={(date) => field.onChange(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: f.label,
                          sx: {
                            '& .MuiPickersInputBase-root': {
                              height: 41,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                );
              case 'select':
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <Select
                      label=""
                      // placeholder={f.label}
                      value={field.value}
                      onChange={field.onChange}
                      options={(f.options || []).map((o) => ({ label: o.label, value: o.value }))}
                      required={false}
                      error={errors?.custom_fields?.[f.key]?.message}
                    />
                  </Box>
                );
              case 'multiselect': {
                // Map options to include optionId for proper label display in chips
                const multiselectOptions = (f.options || []).map((o, idx) => ({
                  optionId: idx,
                  label: o.label,
                  value: o.value,
                }));
                // Convert stored values to indices for MultiSelect
                const valueIndices = Array.isArray(field.value)
                  ? field.value.map((v: string) => {
                    const idx = multiselectOptions.findIndex((opt) => opt.value === v);
                    return idx >= 0 ? idx : v;
                  }).filter((v): v is number => typeof v === 'number')
                  : [];
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <MultiSelect
                      label=""
                      value={valueIndices}
                      onChange={(indices: number[]) => {
                        // Convert indices back to actual values for storage
                        const values = indices.map((idx) => multiselectOptions[idx]?.value).filter(Boolean);
                        field.onChange(values);
                      }}
                      options={multiselectOptions}
                      useOptionId
                      required={false}
                      error={errors?.custom_fields?.[f.key]?.message}
                    />
                  </Box>
                );
              }
              case 'json':
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <JsonEditor
                      label=""
                      name={`custom_fields.${f.key}`}
                      value={field.value}
                      onChange={field.onChange}
                      setError={setError}
                      clearErrors={clearErrors}
                      error={errors?.custom_fields?.[f.key]?.message}
                    />
                  </Box>
                );
              default:
                return (
                  <Box>
                    <FormField label={f.label} tooltip={f.tooltip} required={!!f.required} />
                    <TextField
                      {...field}
                      // placeholder={f.label}
                      {...common}
                      required={false}
                      InputLabelProps={{ shrink: false }}
                    />
                  </Box>
                );
            }
          }}
        />
      ))}
    </Box>
  );
}
